import PropTypes from 'prop-types';
import React from 'react';
import Link from 'next/link';
import { useTranslations, useFormatter } from 'next-intl';

import { isPeriod } from '../../../helper';
import Headline from '../../atoms/headline/headline';
import Copy from '../../atoms/copy/copy';
import Image from '../../atoms/image/image';

import StyledEventTeaser from './eventTeaser.style';
import EventDate from '../event-date/eventDate';

import FilterTag from '../../atoms/filter-tag/filterTag';
import DigitalContentIcon from '../../icons/digitalContent.svg';
import MixedCopy from '../mixed-copy/mixedCopy';

const imageSizes = [
  {
    condition: 'min-width: 980px',
    width: '30vw'
  },
  {
    condition: 'min-width: 450px',
    width: '45vw'
  },
  {
    width: '100vw'
  }
];

const EventTeaser = props => {
  const {
    size = 's',
    uri,
    headline,
    subline,
    startDate,
    endDate,
    pageTags,
    image,
    typeTags = [],
    eventNote,
    fullWidth = false,
    sliderItem,
    copy,
    withImgPlaceholder = false,
    timeOnTop = false,
    bookable = true,
    allDay = false,
    button
  } = props;

  // 1. Initialize next-intl hooks
  const t = useTranslations();
  const format = useFormatter();

  const period = startDate && endDate && isPeriod(startDate, endDate);
  const hasTime =
    startDate && endDate && startDate.H && startDate.i && endDate.H && endDate.i && !allDay;

  // Check if typeTags contain tags starting with #futurium
  // then show digital content icon
  const isDigitalContent =
    typeTags &&
    typeTags.length > 0 &&
    typeTags.filter(item => {
      return item.title.toLowerCase().startsWith('#futurium');
    }).length > 0;

  const eventTeaserContent = (
    <>
      <div className="eventTeaser__categories">
        {bookable && pageTags && (
          <div className="eventTeaser__page-tags">
            {pageTags.map(item => (
              <FilterTag key={item.id} inverted>
                {item.title}
              </FilterTag>
            ))}
          </div>
        )}
        {!bookable && sliderItem && (
          <FilterTag border>
            {/* 2. Replaced <FormattedMessage /> */}
            {t('calendar.soldOut')}
          </FilterTag>
        )}
      </div>
      <div className="eventTeaser__header">
        {image && !timeOnTop && (
          <div className="eventTeaser__image">
            <Image {...image} sizes={imageSizes} />
          </div>
        )}
        {startDate && endDate && (
          <div className="eventTeaser__date">
            <EventDate
              timeOnTop={timeOnTop}
              hasTime={hasTime}
              period={period}
              startDate={startDate}
              endDate={endDate}
              isSmall={sliderItem}
              isTeaser
            />
            {hasTime && timeOnTop && (
              <time className="eventTeaser__time-on-top">
                {/* 3. Replaced <FormattedTime /> with useFormatter hook */}
                {format.dateTime(new Date(startDate.c), {
                  hour: 'numeric',
                  minute: 'numeric'
                })}{' '}
                – {format.dateTime(new Date(endDate.c), { hour: 'numeric', minute: 'numeric' })}
              </time>
            )}
          </div>
        )}
        {!hasTime && !eventNote && !allDay && <div className="eventTeaser__spacer" />}
      </div>

      <div className="eventTeaser__content">
        {isDigitalContent && (
          <DigitalContentIcon className="eventTeaser__icon" width="48px" height="40px" />
        )}
        <div className="eventTeaser__content-inner">
          {eventNote ? (
            <span className="eventTeaser__note">{eventNote}</span>
          ) : (
            <>
              {hasTime && !timeOnTop && (
                <time className="eventTeaser__time">
                  {/* 4. Replaced <FormattedTime /> with useFormatter hook here as well */}
                  {format.dateTime(new Date(startDate.c), {
                    hour: 'numeric',
                    minute: 'numeric'
                  })}{' '}
                  – {format.dateTime(new Date(endDate.c), { hour: 'numeric', minute: 'numeric' })}
                </time>
              )}
            </>
          )}
          {sliderItem ? (
            <Headline tag="h3">{headline}</Headline>
          ) : (
            <Headline teaser>{headline}</Headline>
          )}
          {subline && <Copy>{subline}</Copy>}
          {copy && <MixedCopy className="eventTeaser__copy" fullWidth html={copy} />}
          {button && uri && (
            <div className="eventTeaser__button-container">
              <Link href={uri} title="Mehr details" className="eventTeaser__ctaMore">
                {button}
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
  return (
    <StyledEventTeaser
      {...props}
      sliderItem={sliderItem}
      timeOnTop={timeOnTop}
      fullWidth={fullWidth}
      size={size}
      hasImage={image}
      hasImageFallback={withImgPlaceholder}
      period={period}
    >
      {uri ? (
        <Link href={uri} className="eventTeaser__link">
          {eventTeaserContent}
        </Link>
      ) : (
        <div className="eventTeaser__link">{eventTeaserContent}</div>
      )}
    </StyledEventTeaser>
  );
};

EventTeaser.propTypes = {
  bookable: PropTypes.bool,
  button: PropTypes.string,
  copy: PropTypes.string,
  endDate: PropTypes.shape({
    d: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    H: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    i: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    c: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  eventNote: PropTypes.string,
  fullWidth: PropTypes.bool,
  headline: PropTypes.string.isRequired,
  image: PropTypes.shape(),
  pageTags: PropTypes.arrayOf(PropTypes.shape()),
  size: PropTypes.oneOf(['xs', 's']),
  sliderItem: PropTypes.bool,
  allDay: PropTypes.bool,
  slug: PropTypes.string,
  startDate: PropTypes.shape({
    d: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    H: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    i: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    c: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  subline: PropTypes.string,
  timeOnTop: PropTypes.bool,
  typeTags: PropTypes.arrayOf(PropTypes.shape()),
  uri: PropTypes.string,
  withImgPlaceholder: PropTypes.bool
};

export default EventTeaser;
